import React, { useRef } from 'react';
import { FileText, Download, Calendar, CreditCard, Users, ShoppingCart, DollarSign, Video, Mail, Smartphone, Key, Settings, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

export const SystemManual = () => {
  const printRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-8 print:space-y-4" ref={printRef} data-testid="system-manual">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 print:hidden">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase">Manual del Sistema</h2>
          <p className="text-muted-foreground">Documentación completa de City Virtual Golf</p>
        </div>
        <Button onClick={handlePrint} className="bg-primary">
          <Download className="h-4 w-4 mr-2" />
          Descargar PDF
        </Button>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-3xl font-bold">CITY VIRTUAL GOLF</h1>
        <h2 className="text-xl">Manual del Sistema</h2>
        <p className="text-sm text-gray-500">Última actualización: {new Date().toLocaleDateString('es-AR')}</p>
      </div>

      {/* Índice */}
      <Card className="glass-effect border-white/10 print:border print:border-gray-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Índice de Contenidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li><a href="#info-general" className="text-primary hover:underline">Información General del Sistema</a></li>
            <li><a href="#credenciales" className="text-primary hover:underline">Credenciales y Accesos</a></li>
            <li><a href="#usuarios" className="text-primary hover:underline">Funcionalidades para Usuarios</a></li>
            <li><a href="#admin" className="text-primary hover:underline">Panel de Administración</a></li>
            <li><a href="#pagos" className="text-primary hover:underline">Sistema de Pagos (MercadoPago)</a></li>
            <li><a href="#emails" className="text-primary hover:underline">Sistema de Emails</a></li>
            <li><a href="#planes" className="text-primary hover:underline">Planes y Créditos</a></li>
          </ol>
        </CardContent>
      </Card>

      {/* 1. Información General */}
      <Card id="info-general" className="glass-effect border-white/10 print:border print:border-gray-300 print:break-inside-avoid">
        <CardHeader>
          <CardTitle>1. Información General del Sistema</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            <strong>City Virtual Golf</strong> es una aplicación web diseñada para gestionar un centro de golf indoor 
            ubicado en San Miguel de Tucumán, Argentina. El sistema permite:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Registro y gestión de usuarios con diferentes planes de membresía</li>
            <li>Reserva de turnos mediante un sistema de créditos</li>
            <li>Análisis de swing con Inteligencia Artificial</li>
            <li>Punto de venta (POS) para productos</li>
            <li>Gestión financiera completa (ventas y gastos)</li>
            <li>Pagos online mediante MercadoPago</li>
          </ul>
          
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">Datos Técnicos:</h4>
            <ul className="text-sm space-y-1">
              <li>• <strong>URL:</strong> https://swing-manual.preview.emergentagent.com</li>
              <li>• <strong>Zona Horaria:</strong> Argentina (GMT-3)</li>
              <li>• <strong>Moneda:</strong> Pesos Argentinos (ARS)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* 2. Credenciales */}
      <Card id="credenciales" className="glass-effect border-white/10 print:border print:border-gray-300 print:break-inside-avoid">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            2. Credenciales y Accesos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Admin Access */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg print:bg-yellow-50 print:border-yellow-300">
            <h4 className="font-semibold text-yellow-400 print:text-yellow-700 mb-2">⚠️ Acceso Administrador</h4>
            <ul className="text-sm space-y-1">
              <li><strong>Email:</strong> admin@cityvirtualgolf.com</li>
              <li><strong>Contraseña:</strong> admin123</li>
            </ul>
            <p className="text-xs mt-2 text-yellow-300 print:text-yellow-600">
              * Se recomienda cambiar la contraseña después del primer acceso
            </p>
          </div>

          {/* MercadoPago */}
          <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg print:bg-blue-50 print:border-blue-300">
            <h4 className="font-semibold text-blue-400 print:text-blue-700 mb-2 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              MercadoPago (Pagos Online)
            </h4>
            <ul className="text-sm space-y-1">
              <li><strong>Public Key:</strong> APP_USR-50704569-9f1f-4310-8098-f0975b4014d0</li>
              <li><strong>Access Token:</strong> APP_USR-786605452510***** (oculto por seguridad)</li>
              <li><strong>Tipo:</strong> Credenciales de Producción</li>
            </ul>
            <p className="text-xs mt-2 text-muted-foreground">
              Panel de control: <a href="https://www.mercadopago.com.ar/developers/panel" target="_blank" rel="noopener noreferrer" className="text-blue-400">mercadopago.com.ar/developers</a>
            </p>
          </div>

          {/* Gmail */}
          <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg print:bg-red-50 print:border-red-300">
            <h4 className="font-semibold text-red-400 print:text-red-700 mb-2 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email (Gmail SMTP)
            </h4>
            <ul className="text-sm space-y-1">
              <li><strong>Cuenta:</strong> cityvirtualgolf@gmail.com</li>
              <li><strong>Uso:</strong> Envío de confirmaciones de reserva</li>
              <li><strong>Tipo:</strong> Contraseña de Aplicación configurada</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* 3. Funcionalidades Usuario */}
      <Card id="usuarios" className="glass-effect border-white/10 print:border print:border-gray-300 print:break-before-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            3. Funcionalidades para Usuarios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dashboard */}
          <div>
            <h4 className="font-semibold mb-2">3.1 Dashboard (Página Principal)</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Al iniciar sesión, el usuario ve su panel principal con:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 ml-4">
              <li>Créditos disponibles para reservar turnos</li>
              <li>Cantidad de análisis de video realizados</li>
              <li>Próximos turnos reservados</li>
              <li>Plan actual de membresía</li>
            </ul>
            <img src="/docs/dashboard.png" alt="Dashboard" className="mt-3 rounded-lg border border-white/10 max-w-full print:max-w-[500px]" />
          </div>

          {/* Reserva de Turnos */}
          <div>
            <h4 className="font-semibold mb-2">3.2 Reserva de Turnos</h4>
            <p className="text-sm text-muted-foreground mb-3">
              El sistema permite reservar turnos de práctica de 1 hora cada uno:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 ml-4">
              <li><strong>1 crédito = 1 hora</strong> de práctica</li>
              <li>Selección de fecha en calendario visual</li>
              <li>Horarios de 08:00 a 21:00 (sujeto a disponibilidad)</li>
              <li>Confirmación instantánea</li>
              <li>Email de confirmación con link para calendario</li>
              <li>Opción de compartir por WhatsApp</li>
            </ul>
            <img src="/docs/booking.png" alt="Reserva de Turnos" className="mt-3 rounded-lg border border-white/10 max-w-full print:max-w-[500px]" />
          </div>

          {/* Billetera */}
          <div>
            <h4 className="font-semibold mb-2">3.3 Billetera Virtual</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Los usuarios pueden comprar créditos de dos formas:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 ml-4">
              <li><strong>MercadoPago:</strong> Pago online instantáneo (tarjeta, transferencia, efectivo)</li>
              <li><strong>Transferencia:</strong> Solicitud manual que el admin debe aprobar</li>
              <li>Precio por crédito: <strong>$35.000 ARS</strong></li>
            </ul>
          </div>

          {/* Perfil */}
          <div>
            <h4 className="font-semibold mb-2">3.4 Perfil de Usuario</h4>
            <ul className="list-disc list-inside text-sm space-y-1 ml-4">
              <li>Foto de perfil personalizable</li>
              <li>Nombre y datos personales</li>
              <li>Nivel de habilidad y handicap</li>
              <li>Integración con Google Calendar</li>
            </ul>
            <img src="/docs/profile.png" alt="Perfil" className="mt-3 rounded-lg border border-white/10 max-w-full print:max-w-[500px]" />
          </div>
        </CardContent>
      </Card>

      {/* 4. Panel de Administración */}
      <Card id="admin" className="glass-effect border-white/10 print:border print:border-gray-300 print:break-before-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            4. Panel de Administración
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            El panel de administración tiene <strong>9 secciones</strong> accesibles mediante pestañas:
          </p>
          <img src="/docs/admin_users.png" alt="Panel Admin" className="rounded-lg border border-white/10 max-w-full print:max-w-[500px]" />

          {/* Usuarios */}
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">4.1 Gestión de Usuarios</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Ver todos los usuarios registrados</li>
              <li>Filtrar por estado (Activo, Pasivo, Eventual, Eliminado)</li>
              <li>Buscar por nombre o email</li>
              <li>Editar datos, agregar créditos, cambiar plan</li>
              <li>Agregar notas privadas</li>
              <li>Promover/revocar permisos de administrador</li>
            </ul>
          </div>

          {/* Calendario */}
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">4.2 Calendario de Reservas</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Vista de todas las reservas por día</li>
              <li>Cancelar reservas (devuelve crédito al usuario)</li>
              <li>Ver estado de cada reserva</li>
            </ul>
          </div>

          {/* Disponibilidad */}
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">4.3 Disponibilidad</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Bloquear días completos (feriados, mantenimiento)</li>
              <li>Bloquear horarios específicos</li>
              <li>Crear bloqueos recurrentes (ej: domingos cerrado)</li>
            </ul>
          </div>

          {/* Stock */}
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">4.4 Control de Stock</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Gestión de inventario (bebidas, café, etc.)</li>
              <li>Agregar/editar/eliminar productos</li>
              <li>Ajustar cantidades (entrada, salida, ajuste)</li>
              <li>Alertas de stock bajo</li>
            </ul>
            <img src="/docs/admin_stock.png" alt="Control de Stock" className="mt-3 rounded-lg border border-white/10 max-w-full print:max-w-[400px]" />
          </div>

          {/* Ventas */}
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">4.5 Punto de Venta (POS)</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Seleccionar productos del inventario</li>
              <li>Carrito de compras con cantidades</li>
              <li>Aplicar descuentos</li>
              <li><strong>4 métodos de pago:</strong> Efectivo, Transferencia, Tarjeta, MercadoPago</li>
              <li>Descuento automático de stock</li>
            </ul>
            <img src="/docs/admin_ventas.png" alt="Punto de Venta" className="mt-3 rounded-lg border border-white/10 max-w-full print:max-w-[400px]" />
          </div>

          {/* Finanzas */}
          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">4.6 Dashboard Financiero</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><strong>Filtros:</strong> Hoy, Esta Semana, Este Mes, Este Año, Personalizado</li>
              <li><strong>Ventas:</strong> Total de ingresos por venta de productos y créditos</li>
              <li><strong>Gastos:</strong> Total de egresos (se cargan manualmente)</li>
              <li><strong>Balance:</strong> Diferencia entre ventas y gastos</li>
              <li>Desglose por tipo de venta y categoría de gasto</li>
            </ul>
            <img src="/docs/admin_finanzas.png" alt="Dashboard Financiero" className="mt-3 rounded-lg border border-white/10 max-w-full print:max-w-[400px]" />
          </div>
        </CardContent>
      </Card>

      {/* 5. Sistema de Pagos */}
      <Card id="pagos" className="glass-effect border-white/10 print:border print:border-gray-300 print:break-before-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            5. Sistema de Pagos (MercadoPago)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            El sistema está integrado con <strong>MercadoPago</strong> para procesar pagos online de forma segura.
          </p>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Tipos de Pago Soportados:</h4>
              <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                <li><strong>Compra de Créditos:</strong> Los usuarios pueden comprar créditos desde $35.000</li>
                <li><strong>Venta de Productos:</strong> Desde el POS se puede cobrar con MercadoPago</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Flujo de Pago:</h4>
              <ol className="list-decimal list-inside text-sm space-y-1 ml-4">
                <li>Usuario hace clic en "Pagar con MercadoPago"</li>
                <li>Se redirige a la pasarela segura de MercadoPago</li>
                <li>El usuario elige su método de pago (tarjeta, transferencia, etc.)</li>
                <li>Al completar, vuelve a la aplicación</li>
                <li>Los créditos se acreditan automáticamente</li>
              </ol>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg print:bg-green-50">
              <h4 className="font-semibold text-green-400 print:text-green-700 mb-2 flex items-center gap-2">
                <Check className="h-4 w-4" />
                Webhook Automático
              </h4>
              <p className="text-sm">
                Cuando MercadoPago confirma un pago, el sistema recibe una notificación automática 
                (webhook) y acredita los créditos al usuario sin intervención manual.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 6. Sistema de Emails */}
      <Card id="emails" className="glass-effect border-white/10 print:border print:border-gray-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            6. Sistema de Emails
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Todos los emails del sistema se envían desde <strong>cityvirtualgolf@gmail.com</strong>
          </p>

          <div>
            <h4 className="font-semibold mb-2">Emails Automáticos:</h4>
            <ul className="list-disc list-inside text-sm space-y-1 ml-4">
              <li><strong>Confirmación de Reserva:</strong> Se envía al confirmar un turno</li>
              <li><strong>Cancelación de Reserva:</strong> Se envía al cancelar</li>
              <li><strong>Recuperación de Contraseña:</strong> Link para restablecer contraseña</li>
            </ul>
          </div>

          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">Contenido del Email de Confirmación:</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Fecha y hora del turno</li>
              <li>Links para agregar a Google Calendar, Outlook, iCal</li>
              <li>Link de cancelación</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* 7. Planes y Créditos */}
      <Card id="planes" className="glass-effect border-white/10 print:border print:border-gray-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            7. Planes y Créditos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            El sistema maneja membresías con diferentes beneficios:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg print:bg-blue-50">
              <h4 className="font-semibold text-blue-400 print:text-blue-700">Plan Inicial</h4>
              <ul className="text-sm mt-2 space-y-1">
                <li>• 4 créditos/mes</li>
                <li>• Sin análisis de IA</li>
              </ul>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg print:bg-green-50">
              <h4 className="font-semibold text-green-400 print:text-green-700">Plan Básico</h4>
              <ul className="text-sm mt-2 space-y-1">
                <li>• 8 créditos/mes</li>
                <li>• Sin análisis de IA</li>
              </ul>
            </div>

            <div className="bg-purple-500/10 border border-purple-500/30 p-4 rounded-lg print:bg-purple-50">
              <h4 className="font-semibold text-purple-400 print:text-purple-700">Plan Socio</h4>
              <ul className="text-sm mt-2 space-y-1">
                <li>• 12 créditos/mes</li>
                <li>• <strong>Análisis de Swing con IA</strong></li>
              </ul>
            </div>
          </div>

          <div className="bg-white/5 p-4 rounded-lg print:bg-gray-100">
            <h4 className="font-semibold mb-2">Sistema de Créditos:</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><strong>1 crédito = 1 hora</strong> de práctica</li>
              <li>Precio individual: <strong>$35.000 ARS</strong></li>
              <li>Los créditos no vencen</li>
              <li>Al cancelar una reserva (hasta 1 hora antes), se devuelve el crédito</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground print:mt-8">
        <p>City Virtual Golf © {new Date().getFullYear()}</p>
        <p>San Miguel de Tucumán, Argentina</p>
        <p className="mt-2">Documento generado el {new Date().toLocaleDateString('es-AR', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}</p>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .glass-effect {
            background: white !important;
            border: 1px solid #ddd !important;
          }
          nav, [data-testid="navbar"], .print\\:hidden {
            display: none !important;
          }
          img {
            max-width: 100% !important;
            height: auto !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SystemManual;
